import React, { useEffect, useState } from 'react';
import {
    Container,
    Typography,
    Box,
    CircularProgress,
    Alert,
    Paper,
    Rating,
    Button,
    Card,
    CardContent,
    CardMedia,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { request } from '../../Utils/request';
import TopNavBar from '../../Components/TopNavigation/TopNavBar';
import { calculateDistance } from '../../Utils/googleMapsDistance';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducers';

interface MyReview {
    _id: string;
    user: {
        _id: string;
        username: string;
        profilePhoto: {
            url: string;
        };
    };
    rating: number;
    review: string;
    ratedAt: string;
    place: {
        _id: string;
        name: string;
        description: string;
        address: string;
        location: {
            lat: number;
            lng: number;
            name: string;
        };
        categories: string[];
        files: Array<{
            fileKey: string;
            fileType: string;
            featured: boolean;
            fileURL: string;
        }>;
    };
}

interface MyReviewsResponse {
    success: boolean;
    data: {
        reviews: MyReview[];
    };
}

const MyReviews: React.FC = () => {
    const navigate = useNavigate();
    const [reviews, setReviews] = useState<MyReview[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [distances, setDistances] = useState<{ [key: string]: number }>({});
    const userLocation = useSelector((state: RootState) => state.location.location);

    const calculateDistances = async (reviews: MyReview[]) => {
        if (!userLocation?.lat || !userLocation?.lng) return;

        const distancePromises = reviews.map(async (review) => {
            try {
                const distance = await calculateDistance(
                    userLocation.lat,
                    userLocation.lng,
                    review.place.location.lat,
                    review.place.location.lng
                );
                return { reviewId: review._id, distance };
            } catch (error) {
                console.error(`Error calculating distance for review ${review._id}:`, error);
                return { reviewId: review._id, distance: null };
            }
        });

        const results = await Promise.all(distancePromises);
        const distanceMap: { [key: string]: number } = {};
        results.forEach(({ reviewId, distance }) => {
            if (distance !== null) {
                distanceMap[reviewId] = distance;
            }
        });
        setDistances(distanceMap);
    };

    useEffect(() => {
        const fetchMyReviews = async () => {
            try {
                setLoading(true);
                const response = await request<MyReviewsResponse>(
                    '/places/my-reviews',
                    { authorization: true }
                );

                if (response.success === true) {
                    setReviews(response.data.reviews);
                    // Calculate distances after reviews are loaded
                    calculateDistances(response.data.reviews);
                } else {
                    setError('Failed to fetch your reviews');
                }
            } catch (err) {
                console.error('Error fetching my reviews:', err);
                setError('Error loading your reviews');
            } finally {
                setLoading(false);
            }
        };

        fetchMyReviews();
    }, []);

    const handleBack = () => {
        navigate(-1);
    };

    if (loading) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
                <Button onClick={handleBack}>
                    Go Back
                </Button>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            {/* Header */}
            <TopNavBar title="My Reviews" canGoBack />

            {/* Reviews List */}
            {reviews.length === 0 ? (
                <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                        No reviews yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        You haven't written any reviews yet. Start exploring places and share your experiences!
                    </Typography>
                </Paper>
            ) : (
                <Box>
                    {reviews.map((review) => {
                        const featuredImage = review.place.files?.find(file => file.featured)?.fileURL ||
                            review.place.files?.[0]?.fileURL;

                        return (
                            <Card
                                key={review._id}
                                sx={{
                                    mb: 2,
                                    borderRadius: 3,
                                    cursor: 'pointer',
                                    '&:hover': {
                                        boxShadow: 4,
                                        transform: 'translateY(-2px)',
                                        transition: 'all 0.2s ease-in-out'
                                    },
                                }}
                                onClick={() => navigate(`/places/${review.place._id}`)}
                            >
                                <CardContent sx={{ px: '12px' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        {/* Place Image */}
                                        <Box sx={{ width: 80, height: 80, flexShrink: 0 }}>
                                            <CardMedia
                                                component="img"
                                                image={featuredImage || '/placeholder-image.jpg'}
                                                alt={review.place.name}
                                                sx={{
                                                    borderRadius: '12px',
                                                    objectFit: 'cover',
                                                    width: '100%',
                                                    height: '100%'
                                                }}
                                            />
                                        </Box>

                                        {/* Content */}
                                        <Box sx={{ flex: 1, px: '8px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between',}}>
                                            <Box>
                                                {/* Date */}
                                                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                                                    {new Date(review.ratedAt).toLocaleDateString(undefined, {
                                                        year: 'numeric',
                                                        month: 'short',
                                                        day: 'numeric'
                                                    })}
                                                </Typography>

                                                {/* Place Name and Distance */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <Typography fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                                                        {review.place.name}
                                                    </Typography>
                                                    {distances[review._id] && (
                                                        <Typography variant="caption"  sx={{ ml: 2 ,color:'#7F7F7F',fontWeight: 500}}>
                                                            {distances[review._id].toFixed(1)} km
                                                        </Typography>
                                                    )}
                                                </Box>


                                            

                                                {/* Review Text */}
                                                <Typography
                                                    variant="body2"
                                                    color="text.primary"
                                                    sx={{
                                                        lineHeight: 1.4,
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}
                                                >
                                                    {review.review}
                                                </Typography>
                                                    {/* Rating */}
                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                    <Rating
                                                        value={review.rating}
                                                        readOnly
                                                        size="small"
                                                        sx={{ mr: 1 }}
                                                    />
                                                    <Typography variant="body2" color="text.secondary">
                                                        {review.rating}/5
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        );
                    })}
                </Box>
            )}
        </Container>
    );
};

export default MyReviews;
